import React, {CSSProperties, useContext, useState} from "react";
import {T} from "@tolgee/react";
import FlexBox from "view/components/Layout/FlexBox";
import Button from "@material-ui/core/Button";
import {KeyboardBackspaceSharp} from "@material-ui/icons";
import {StepOrder} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList";
import {useSelector} from "react-redux";
import {expandOrder} from "tools/order/expandOrder";
import {downloadVatPdf} from "view/components/OrderDrawer/helpers";
import {LocalMapContext} from "view/context/LocalMapContext";

import {selectUser} from "store/auth/selectors";
import DownloadIcon from "@material-ui/icons/GetApp";
import {CircularProgress, Typography, useTheme} from "@material-ui/core";
import {ATTEMPTS_BEFORE_SKIP_PAYMENT} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList/constants";
import {useCustomProductOptionShopStoreState} from "store/custom-product-option/selectors";
import {useShowProductsStore} from "store/product/show-products.store";
import {getOrderCpoConfigs} from "tools/order/getOrderCpoConfigIds";
import {useIsShowDiscountCodesInvalid} from "store/discount-code/discount-code-store";
import {IShow} from "@evennt-tech/api-v2-core";

const iconStyle: CSSProperties = {
  transform: "rotate(180deg)",
};

export type PaymentButtonsProps = {
  onSkip: () => void;
  onPay: () => Promise<void>;
  isLastToPay: boolean;
  isLastToSkip: boolean;
  stepState: StepOrder;
  isPaymentDisabled: boolean;
  isSubmitting: boolean;
  show: IShow;
  standId: string;
};

const PaymentButtons = ({
  onSkip,
  onPay,
  isLastToPay,
  isLastToSkip,
  isPaymentDisabled,
  isSubmitting,
  stepState,
  show,
  standId,
}: PaymentButtonsProps) => {
  const {palette} = useTheme();
  const {error, isPaid, id: orderId, attempts} = stepState;
  const localMap = useContext(LocalMapContext);
  const user = useSelector(selectUser);
  const {productConfigsBySupplier} = useCustomProductOptionShopStoreState();
  const isLoading = useShowProductsStore((state) => state.isLoading);
  const isDiscountCodeInvalid = useIsShowDiscountCodesInvalid(show.id);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleVatDownload = async () => {
    try {
      setIsDownloading(true);
      if (!standId) {
        throw new Error("Invalid stand ID");
      }

      const order = await expandOrder({
        standId,
        showId: show.id,
        exhibitorId: user.id,
        orderId,
      });
      if (!order) {
        throw new Error("Failed to fetch order");
      }
      const configById = getOrderCpoConfigs(productConfigsBySupplier, order);

      await Promise.all(
        (order.supplierCompanies || []).map(async (supplierCompany) => {
          const supplierProduct = order.products.find(
            ({owner}) => owner.id === supplierCompany.id
          );

          return downloadVatPdf(
            supplierCompany,
            supplierProduct?.invoiceId,
            order,
            user,
            show,
            localMap,
            false,
            configById,
            []
          );
        })
      );
    } catch (e) {
      console.error("Error occurred downloading receipt: ", e);
    } finally {
      setIsDownloading(false);
    }
  };

  const shouldShowSkipBtn = !!error && attempts >= ATTEMPTS_BEFORE_SKIP_PAYMENT;

  return (
    <FlexBox alignCenter justifyContent="flex-end" fullWidth gap={2}>
      {shouldShowSkipBtn && (
        <Button
          variant="text"
          endIcon={<KeyboardBackspaceSharp style={iconStyle} />}
          color="primary"
          onClick={onSkip}
          disabled={isSubmitting}
        >
          <T
            keyName={
              isLastToSkip
                ? "exhibitor.cart.payment.skip_and_submit"
                : "exhibitor.cart.payment.skip_and_continue"
            }
          />
        </Button>
      )}
      {!isPaid && (
        <Button
          variant="contained"
          color="primary"
          endIcon={<KeyboardBackspaceSharp style={iconStyle} />}
          onClick={onPay}
          disabled={isPaymentDisabled || isSubmitting || isDiscountCodeInvalid}
        >
          <FlexBox alignCenter gap={2}>
            {isSubmitting && <CircularProgress size={16} />}
            <Typography variant="button">
              <T
                keyName={
                  isLastToPay
                    ? "exhibitor.cart.payment.pay_and_submit"
                    : "exhibitor.cart.payment.pay_and_continue"
                }
              />
            </Typography>
          </FlexBox>
        </Button>
      )}
      {isPaid && (
        <Button
          variant="text"
          color="primary"
          disabled={isDownloading || isLoading}
          onClick={handleVatDownload}
        >
          <FlexBox gap={1} alignCenter>
            <DownloadIcon
              width={24}
              height={24}
              htmlColor={palette.primary.main}
            />
            <Typography variant="button" color="primary">
              <T keyName="exhibitor.cart.payment.download_receipt" />
            </Typography>
          </FlexBox>
        </Button>
      )}
    </FlexBox>
  );
};

export default PaymentButtons;
